import { patchHTML } from "../core/surface-host.js";
import { ensureAdminContentLayout } from "./layout.js";
import { escapeHTML, renderSkeleton, renderTruncatedID } from "../render/ui-helpers.js";

function workspaceSourceKindLabel(value) {
  return value === "local_authoring" ? "Local source folder" : "Remote repo";
}

function workspaceCreateNote(repoSourceKind) {
  if (repoSourceKind === "local_authoring") {
    return "Create a managed workspace. Lytebulb copies the selected folder into its own repo during initialize and sync. Uploads, edits, and commits happen in the managed repo, not back in the selected folder.";
  }
  return "Create the workspace first, then initialize it explicitly from the catalog.";
}

function workspaceSubmitLabel(pageState) {
  if (pageState.isEditing) {
    return "Save";
  }
  return pageState.workspaceDraft.repo_source_kind === "local_authoring" ? "Create managed workspace" : "Create workspace";
}

export function renderWorkspacesRoute(layoutRoot, state, options = {}) {
  ensureAdminContentLayout(layoutRoot, "workspaces", `
    <div class="entity-page">
      <div data-workspaces-form-root></div>
      <div data-workspaces-list-root></div>
    </div>
  `);
  const formRoot = layoutRoot.querySelector("[data-workspaces-form-root]");
  const listRoot = layoutRoot.querySelector("[data-workspaces-list-root]");
  if (!state.apiKey) {
    patchHTML(formRoot, "");
    patchHTML(listRoot, options.renderLockedState?.() || "");
    return;
  }
  if (state.loading) {
    patchHTML(formRoot, "");
    patchHTML(listRoot, renderSkeleton(4));
    return;
  }
  const pageState = buildWorkspacesPageState(state, options);
  patchHTML(formRoot, renderWorkspacesFormSection(state, pageState, options));
  patchHTML(listRoot, renderWorkspacesListSection(state, options));
}

export function buildWorkspacesPageState(state, options = {}) {
  const editingWorkspace = state.editingWorkspaceID ? options.lookupByID?.(state.workspaces, state.editingWorkspaceID) || null : null;
  const editingWorkspaceDiagnostics = editingWorkspace ? options.lookupWorkspaceDiagnostics?.(editingWorkspace.id) || null : null;
  const isEditing = Boolean(editingWorkspace);
  return {
    editingWorkspace,
    editingWorkspaceDiagnostics,
    isEditing,
    showForm: state.workspaceCreateOpen || isEditing,
    workspaceDraft: state.forms.workspace
  };
}

export function renderWorkspacesFormSection(state, pageState, options = {}) {
  if (!pageState.showForm) {
    return "";
  }
  return `
    <div class="entity-form-panel">
      ${pageState.isEditing ? `
        <div class="form-note">Editing <strong>${escapeHTML(pageState.editingWorkspace.name)}</strong> ${renderTruncatedID(pageState.editingWorkspace.id)}</div>
        <div class="form-readonly-strip">
          <span class="form-readonly-item"><span class="form-readonly-label">Source</span> ${escapeHTML(workspaceSourceKindLabel(pageState.editingWorkspace.repo_source_kind || "remote"))}</span>
          ${pageState.editingWorkspace.remote_url ? `<span class="form-readonly-item"><span class="form-readonly-label">Remote</span> ${escapeHTML(pageState.editingWorkspace.remote_url)}</span>` : ""}
          ${pageState.editingWorkspace.remote_name ? `<span class="form-readonly-item"><span class="form-readonly-label">Remote name</span> ${escapeHTML(pageState.editingWorkspace.remote_name)}</span>` : ""}
          ${pageState.editingWorkspaceDiagnostics?.repo_path ? `<span class="form-readonly-item"><span class="form-readonly-label">Managed repo</span> ${escapeHTML(options.truncatePath?.(pageState.editingWorkspaceDiagnostics.repo_path) || pageState.editingWorkspaceDiagnostics.repo_path)}</span>` : ""}
        </div>
      ` : ""}
      <form id="workspace-form" class="form-grid">
        ${pageState.isEditing ? "" : `
          <div class="form-grid form-grid--split">
            <div class="field">
              <label for="workspace-name">Name</label>
              <input id="workspace-name" name="name" required placeholder="Runtime repo" value="${escapeHTML(pageState.workspaceDraft.name)}">
            </div>
            <div class="field">
              <label for="workspace-repo-source-kind">Source kind</label>
              <select id="workspace-repo-source-kind" name="repo_source_kind">
                <option value="remote" ${pageState.workspaceDraft.repo_source_kind === "remote" ? "selected" : ""}>Remote repo</option>
                <option value="local_authoring" ${pageState.workspaceDraft.repo_source_kind === "local_authoring" ? "selected" : ""}>Local source folder</option>
              </select>
            </div>
          </div>
        `}
        ${pageState.isEditing ? `
          <div class="field">
            <label for="workspace-name">Name</label>
            <input id="workspace-name" name="name" required placeholder="Runtime repo" value="${escapeHTML(pageState.workspaceDraft.name)}">
          </div>
        ` : ""}
        ${!pageState.isEditing && pageState.workspaceDraft.repo_source_kind === "remote" ? `
          <div class="field">
            <label for="workspace-remote-url">Remote URL</label>
            <input id="workspace-remote-url" name="remote_url" placeholder="https://example.com/repo.git" value="${escapeHTML(pageState.workspaceDraft.remote_url)}">
          </div>
        ` : ""}
        ${(pageState.isEditing || pageState.workspaceDraft.repo_source_kind === "local_authoring") ? `
          <div class="field">
            <label for="workspace-authoring-root-path">Local source folder</label>
            <input id="workspace-authoring-root-path" name="authoring_root_path" placeholder="/absolute/path/to/local/folder-or-repo" value="${escapeHTML(pageState.workspaceDraft.authoring_root_path)}">
            <div class="field-hint">Lytebulb copies this folder into a managed repo during initialize and sync. Uploads, edits, and commits happen in the managed repo, not back in this folder.</div>
          </div>
        ` : ""}
        <div class="form-grid form-grid--split">
          <div class="field">
            <label for="workspace-default-branch">Default branch</label>
            <input id="workspace-default-branch" name="default_branch" placeholder="main" value="${escapeHTML(pageState.workspaceDraft.default_branch)}">
          </div>
          ${!pageState.isEditing && pageState.workspaceDraft.repo_source_kind === "remote" ? `
            <div class="field">
              <label for="workspace-remote-name">Remote name</label>
              <input id="workspace-remote-name" name="remote_name" placeholder="origin" value="${escapeHTML(pageState.workspaceDraft.remote_name)}">
            </div>
          ` : ""}
        </div>
        ${!pageState.isEditing && pageState.workspaceDraft.repo_source_kind === "remote" ? `
          <div class="form-grid form-grid--split">
            <div class="field">
              <label for="workspace-fetch-auth-ref">Fetch auth ref</label>
              <input id="workspace-fetch-auth-ref" name="fetch_auth_ref" placeholder="gitlab-read" value="${escapeHTML(pageState.workspaceDraft.fetch_auth_ref)}">
            </div>
            <div class="field">
              <label for="workspace-push-auth-ref">Push auth ref</label>
              <input id="workspace-push-auth-ref" name="push_auth_ref" placeholder="gitlab-write" value="${escapeHTML(pageState.workspaceDraft.push_auth_ref)}">
            </div>
          </div>
        ` : ""}
        <div class="form-note">${pageState.isEditing ? "Edit workspace metadata here. Use the row actions to initialize, sync, connect a remote, or configure review." : workspaceCreateNote(pageState.workspaceDraft.repo_source_kind)}</div>
        ${state.formErrors.workspace ? `<div class="form-error">${escapeHTML(state.formErrors.workspace)}</div>` : ""}
        <div class="button-row">
          <button class="button button--primary" type="submit">${workspaceSubmitLabel(pageState)}</button>
          ${pageState.isEditing ? `<button class="button button--ghost" type="button" data-action="cancel-workspace-edit">Cancel</button>` : ""}
        </div>
      </form>
    </div>
  `;
}

export function renderWorkspacesListSection(state, options = {}) {
  return `
    <div class="entity-list">
      ${state.workspaces.length ? state.workspaces.map((item) => {
        const gitBadge = item.git_state || "registered";
        const primary = options.workspacePrimaryAction?.(item) || { action: "initialize-workspace", label: "Initialize" };
        const contentOpen = state.workspaceContent.workspaceID === item.id;
        return `
          <article class="entity-row">
            <div class="entity-row__main">
              <div class="entity-row__body">
                <div class="entity-row__title">${escapeHTML(item.name)}</div>
                <div class="entity-row__meta">${escapeHTML(options.workspacePrimaryLocation?.(item) || item.name)}</div>
                <div class="entity-row__detail">${escapeHTML(options.workspaceStateSummary?.(item) || item.git_state || "workspace")}</div>
                ${options.renderWorkspacePushStatus?.(item) || ""}
                ${options.renderWorkspaceCommitHistory?.(item) || ""}
              </div>
              <div class="entity-row__side">
                <span class="badge">${escapeHTML(gitBadge)}</span>
                <div class="entity-row__actions">
                  <button class="button button--sm button--ghost" type="button" data-action="${escapeHTML(primary.action)}" data-workspace-id="${escapeHTML(item.id)}">${escapeHTML(primary.label)}</button>
                  ${item.git_state === "ready" ? `<button class="button button--sm ${contentOpen ? "button--primary" : "button--ghost"}" type="button" data-action="toggle-workspace-content" data-workspace-id="${escapeHTML(item.id)}">${contentOpen ? "Hide content" : "Content"}</button>` : ""}
                  ${item.remote_connection_state === "connected" && item.git_state === "ready" ? `<button class="button button--sm button--ghost" type="button" data-action="push-workspace" data-workspace-id="${escapeHTML(item.id)}">Push ${escapeHTML(item.default_branch || "main")}</button>` : ""}
                  ${item.repo_source_kind === "local_authoring" && item.remote_connection_state !== "connected" ? `<button class="button button--sm button--ghost" type="button" data-action="connect-workspace-remote" data-workspace-id="${escapeHTML(item.id)}">Connect</button>` : ""}
                  ${item.remote_connection_state === "connected" ? `<button class="button button--sm ${state.reviewConfigWorkspaceID === item.id ? "button--primary" : "button--ghost"}" type="button" data-action="configure-workspace-review" data-workspace-id="${escapeHTML(item.id)}">${item.review_provider ? "Review" : "Setup review"}</button>` : ""}
                  <button class="button button--sm button--ghost" type="button" data-action="edit-workspace" data-workspace-id="${escapeHTML(item.id)}">Edit</button>
                  <button class="button button--sm button--danger" type="button" data-action="delete-workspace" data-workspace-id="${escapeHTML(item.id)}">Delete</button>
                </div>
              </div>
            </div>
            ${state.reviewConfigWorkspaceID === item.id ? options.renderWorkspaceReviewForm?.(item) || "" : ""}
            ${state.pushWorkspaceID === item.id ? options.renderWorkspacePushPanel?.(item) || "" : ""}
            ${contentOpen ? options.renderWorkspaceContentPanelForList?.(item) || "" : ""}
          </article>
        `;
      }).join("") : `
        <div class="entity-empty">
          <div class="entity-empty__title">No workspaces yet</div>
          <div class="entity-empty__copy">Register a git repository to start working with threads.</div>
          <button class="button button--primary" type="button" data-action="toggle-workspace-create">+ New workspace</button>
        </div>
      `}
    </div>
  `;
}
